/**
 * useTipologiaColori — legge /config_tipologie/appuntamento e risolve
 * la mappa tipologia → { bar, bg, label } (PR-I Fase 5).
 *
 * SINGLE SOURCE OF TRUTH a runtime: Firestore con fallback silenzioso
 * ai default di `tipologia-palette.ts` (D7). staleTime 5 min, retry 0
 * (errore → default subito, senza allungare il gate agenda).
 *
 * State condiviso via QueryClient di radice (nessun useState per-istanza).
 */

import { useQuery } from '@tanstack/react-query';
import { doc, getDoc } from 'firebase/firestore';
import {
  CONFIG_TIPOLOGIE_COLLECTION,
  CONFIG_TIPOLOGIE_DOC_ID,
  CONFIG_TIPOLOGIE_FIELD,
} from '@shared/constants/config-tipologie';
import {
  PALETTE_TOKEN_IDS,
  TIPOLOGIA_COLOR_DEFAULTS,
  TIPOLOGIA_KEYS,
  resolveTipologiaColori,
  type PaletteTokenId,
  type TipologiaColorResolved,
  type TipologiaKey,
} from '@shared/constants/tipologia-palette';
import { firestore } from '../security/firebase';
import { useAuth } from './useAuth';

export const TIPOLOGIA_COLORI_QUERY_KEY = ['tipologia-colori'] as const;

export type TipologiaColoriMap = Readonly<
  Record<TipologiaKey, TipologiaColorResolved>
>;

export type TipologiaTokenIdsMap = Readonly<
  Record<TipologiaKey, PaletteTokenId>
>;

/** Default risolti (config assente). Usati anche come valore Context iniziale. */
export const DEFAULT_TIPOLOGIA_COLORI: TipologiaColoriMap =
  resolveTipologiaColori(null);

function isPaletteTokenId(value: string): value is PaletteTokenId {
  return (PALETTE_TOKEN_IDS as ReadonlyArray<string>).includes(value);
}

/**
 * Risolve la mappa completa tipologia → id token (con fallback ai default).
 * Vive lato client: non tocca la fonte unica (C1 — niente regen del generated).
 */
export function resolveTipologiaTokenIds(
  config: Partial<Record<TipologiaKey, string>> | null | undefined
): TipologiaTokenIdsMap {
  const out = {} as Record<TipologiaKey, PaletteTokenId>;
  for (const key of TIPOLOGIA_KEYS) {
    const raw = config?.[key];
    out[key] =
      typeof raw === 'string' && isPaletteTokenId(raw)
        ? raw
        : TIPOLOGIA_COLOR_DEFAULTS[key];
  }
  return out;
}

export const DEFAULT_TIPOLOGIA_TOKEN_IDS: TipologiaTokenIdsMap =
  resolveTipologiaTokenIds(null);

function parseConfigField(
  raw: unknown
): Partial<Record<TipologiaKey, string>> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  return raw as Partial<Record<TipologiaKey, string>>;
}

export interface TipologiaColoriFetchResult {
  resolved: TipologiaColoriMap;
  tokenIds: TipologiaTokenIdsMap;
}

/**
 * Fetch + resolve. Qualunque errore / doc assente / shape invalida → default.
 * Mai throw: il caller riceve sempre mappe complete (D7).
 */
export async function fetchTipologiaColoriResolved(): Promise<TipologiaColoriMap> {
  return (await fetchTipologiaColoriFull()).resolved;
}

/** Fetch completo: colori risolti + id token (per la pagina Impostazioni). */
export async function fetchTipologiaColoriFull(): Promise<TipologiaColoriFetchResult> {
  try {
    const snap = await getDoc(
      doc(firestore, CONFIG_TIPOLOGIE_COLLECTION, CONFIG_TIPOLOGIE_DOC_ID)
    );
    if (!snap.exists()) {
      return {
        resolved: DEFAULT_TIPOLOGIA_COLORI,
        tokenIds: DEFAULT_TIPOLOGIA_TOKEN_IDS,
      };
    }
    const config = parseConfigField(snap.data()?.[CONFIG_TIPOLOGIE_FIELD]);
    return {
      resolved: resolveTipologiaColori(config),
      tokenIds: resolveTipologiaTokenIds(config),
    };
  } catch {
    return {
      resolved: DEFAULT_TIPOLOGIA_COLORI,
      tokenIds: DEFAULT_TIPOLOGIA_TOKEN_IDS,
    };
  }
}

function isStudioRole(role: string | null | undefined): boolean {
  return (
    role === 'TITOLARE' ||
    role === 'DIETOLOGA' ||
    role === 'SEGRETERIA' ||
    role === 'ADMIN'
  );
}

/**
 * Query condivisa. `enabled` solo per ruoli studio (D6: PAZIENTE non legge).
 * Espone `isLoading` per il gate anti-flash di AgendaPage.
 */
export function useTipologiaColori() {
  const { role } = useAuth();
  return useQuery({
    queryKey: TIPOLOGIA_COLORI_QUERY_KEY,
    queryFn: fetchTipologiaColoriResolved,
    staleTime: 5 * 60_000,
    retry: 0,
    enabled: isStudioRole(role),
  });
}
